<template>
  <div class="arttextstyle">
    <div class="arttextstyle__wrapper">
      <div
        class="arttextstyle__background"
        :style="{
          backgroundImage: `url(${featuredPost.header_image})`,
        }"
      ></div>
      <div class="arttextstyle__main-container">
        <div class="arttextstyle__details-box">
          <h2 class="arttextstyle__details-box_title">
            {{ featuredPost.title }}
          </h2>
          <p class="arttextstyle__details-box_data">
            <span>{{
              featuredPost.date_published | _moment("MMM DD YYYY", "YYYY-MM-DD, hh:mm a")
            }}</span>
            <span class="arttextstyle__details-box_data--devider"> | </span>
            <span> by arttextstyle </span>

            <span class="arttextstyle__details-box_data--devider"> | </span>
            <span>{{ featuredPost.read_time || "5min" }} read</span>
          </p>

          <div v-html="featuredPost.details" class="arttextstyle__details-box_details"></div>
          <BaseButton
            class="button button--xs button--white"
            text=" READ ARTICLE"
            @click="$router.push({ path: `/media/arttextstyle/${featuredPost.slug}` })"
          />
        </div>

        <div class="arttextstyle__title-box" ref="titleBox">
          <h2 class="arttextstyle__title-box_title">arttextstyle</h2>
          <div class="arttextstyle__title-box_right">
            <div
              class="arttextstyle__title-box_right-content arttextstyle__title-box_right-content--desktop"
            >
              <Tag
                class="tag--public"
                :class="{ 'tag--selected': item.selected }"
                v-for="(item, index) in getVisibleTags"
                :key="`tag-${index}`"
                :value="item.title"
                @click="changeFilterValue(item)"
              />
              <AdditionalInfo
                v-if="getHiddenTags.length"
                class="additional-tags--public"
                :items="getHiddenTags"
                :parentClientRect="tagsClientRect"
                :isPublic="true"
                :isSearch="true"
                @getParentClientRect="getParentClientRect"
                @changeFilterValue="changeFilterValue"
              ></AdditionalInfo>
            </div>
            <div
              class="arttextstyle__title-box_right-content arttextstyle__title-box_right-content--mobile"
            >
              <AdditionalInfo
                v-if="tagsData.length"
                class="additional-tags--public"
                :items="tagsData"
                :parentClientRect="tagsClientRect"
                :isPublic="true"
                :isSearch="true"
                :isFilter="true"
                @getParentClientRect="getParentClientRect"
                @changeFilterValue="changeFilterValue"
              ></AdditionalInfo>
            </div>
            <div class="divider divider--vertical"></div>
            <div class="arttextstyle__title-box_right-content">
              <PublicSelect
                :items="sortData"
                :selected="cardData.sort"
                @input="selectSort"
              ></PublicSelect>
            </div>
          </div>
        </div>
        <ArttextstyleList v-if="arttextstyles.length" :arttextstyles="arttextstyles" />
        <h6 v-else class="arttextstyle__empty">No Arttextstyle</h6>
        <div class="arttextstyle__button-box" v-if="showLoadMoreButton && hasMorePages">
          <BaseButton
            class="button button--xs button--white"
            text="LOAD MORE"
            @click.native="handleLoadMoreClick"
          />
        </div>
      </div>
    </div>
    <div ref="infiniteTrigger" style="height: 1px"></div>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from "vuex";
import BaseButton from "@/components/buttons/BaseButton.vue";
import PublicSelect from "@/components/inputs/PublicSelect.vue";
import Tag from "@/components/Tag.vue";
import AdditionalInfo from "@/components/AdditionalInfo.vue";
import useInfiniteScroll from "@/composables/useInfiniteScroll.js";
import ArttextstyleList from "@/views/public/library/arttextstyle/components/ArttextstyleList.vue";
import useSeoHead from "@/composables/useSeoHead";

export default {
  components: { BaseButton, PublicSelect, Tag, AdditionalInfo, ArttextstyleList },

  data() {
    return {
      tagsClientRect: { bottom: 0, right: 0 },
      sortData: [
        { id: "newest_to_oldest", title: "Newest to Oldest" },
        { id: "oldest_to_newest", title: "Oldest to Newest" },
        { id: "title_a_z", title: "Title (A-Z)" },
        { id: "title_z_a", title: "Title (Z-A)" },
        { id: "author_a_z", title: "Author (A-Z)" },
        { id: "author_z_a", title: "Author (Z-A)" },
      ],
      cardData: {
        sort: "newest_to_oldest",
        tags: "",
        page: 1,
      },
      showLoadMoreButton: true,
      loading: false,
    };
  },
  head() {
    return useSeoHead({
      title: "Arttextstyle browngrotta arts",
      description: "Description Arttextstyle browngrotta arts",
      image: "/img/icons/favicon-500x500.png",
      routePath: this.$route.path,
    });
  },
  async created() {
    try {
      await Promise.all([this.apiGetArttextstyles(this.cardData), this.apiGetFiltersBlock()]);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        this.$router.push("/not-found");
      }
    }

    this.setBreadcrumbs([
      { title: "Home", to: "/" },
      { title: "Media", to: "/media", clickable: true },
      { title: "Arttextstyle", to: "/media/arttextstyle", clickable: false },
    ]);
  },
  watch: {
    tagsData: {
      handler: function (val, oldVal) {
        let type = "";
        val.forEach(el => {
          if (el.selected) {
            type = el.id === "" ? "" : el.id;
          }
        });

        this.cardData.type = type;
        this.selectTypes(type);
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters("publicapi/blogs", {
      arttextstyles: "getArttextstyles",
      hasMorePages: "getHasMorePages",
      featuredPost: "getFeaturedPost",
      tagsData: "getFilters",
    }),

    getVisibleTags() {
      return this.tagsData.slice(0, 5);
    },

    getHiddenTags() {
      return this.tagsData.slice(5, this.tagsData.length);
    },
  },
  methods: {
    ...mapMutations(["setBreadcrumbs"]),
    ...mapMutations("publicapi/blogs", ["changeFilterValue"]),
    ...mapActions("publicapi/blogs", ["apiGetArttextstyles", "apiGetFiltersBlock"]),

    getParentClientRect() {
      this.tagsClientRect = this.$refs.titleBox.getBoundingClientRect();
    },

    async loadMore() {
      if (this.loading || !this.hasMorePages) return;
      this.loading = true;

      let data = { ...this.cardData, page: this.cardData.page + 1 };
      let success = await this.apiGetArttextstyles(data);
      if (success) {
        this.cardData = data;
      }
      this.loading = false;
    },
    handleLoadMoreClick() {
      this.showLoadMoreButton = false;
      this.loadMore();
      this.infiniteScroll.enableInfiniteScroll();
    },

    async selectTypes(types) {
      let data = { ...this.cardData, page: 1, tags: types };
      let success = await this.apiGetArttextstyles(data);
      if (success) {
        this.cardData = data;
      }
    },
    async selectSort({ id }) {
      let data = { ...this.cardData, sort: id, page: 1 };
      let success = await this.apiGetArttextstyles(data);
      if (success) {
        this.cardData = { ...data };
      }
    },
  },
  mounted() {
    this.infiniteScroll = useInfiniteScroll(this);
    if (!this.showLoadMoreButton) {
      this.infiniteScroll.enableInfiniteScroll();
    }
  },
  beforeDestroy() {
    this.infiniteScroll.disableInfiniteScroll();
  },
};
</script>

<style lang="scss" scoped>
* {
  color: $white;
  font-size: 1.8rem;
  line-height: 2.4rem;
  @media screen and (max-width: $xs) {
    font-size: 1.6rem;
    line-height: 2.5rem;
  }
}

.arttextstyle {
  display: flex;

  &__background {
    @include backgroundBlock();
  }

  &__wrapper {
    @include fullScreenWrapper;
  }

  &__main-container {
    @include mainContainerCenteredCol(0rem 10.5rem 23.5rem, 40rem);

    @media screen and (max-width: $md) {
      margin-top: 24.5rem;
      padding: 0rem 8rem 7.4rem;
    }

    @media screen and (max-width: $sm) {
      padding: 0rem 3rem 7.4rem;
    }

    @media screen and (max-width: $xs) {
      padding: 0rem 25px 11rem;
    }
  }

  &__details-box {
    display: flex;
    flex-direction: column;
    width: 72%;
    @media screen and (max-width: $md) {
      width: 100%;
    }
    .button {
      margin-bottom: 15px;
    }
    &_title {
      font-size: 5.2rem;
      line-height: 1.25;
      @media only screen and (max-width: $md) {
        font-size: 35px;
      }
    }
    &_data {
      display: flex;
      font-size: 14px;
      line-height: 1.8;
      text-transform: uppercase;
      margin-bottom: 25px;
      &--devider {
        text-align: center;
        width: 25px;
        display: block;
      }
      @media screen and (max-width: $xs) {
        font-size: 14px;
      }
    }
    &_details {
      font-size: 2.35rem;
      line-height: 1.55;
      margin-bottom: 35px;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }

  &__title-box {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    width: 100%;
    margin: 9rem 0rem 6rem;
    gap: 20px;
    @media screen and (max-width: $xs) {
      margin: 8.5rem 0rem 2rem;
    }
    &_right {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      flex-wrap: wrap;
      @media screen and (max-width: $lg) {
        flex-direction: row-reverse;
        justify-content: flex-start;
        flex-wrap: nowrap;
        gap: 0 10px;
        .divider {
          display: none;
        }
      }
      @media screen and (max-width: $sm) {
        width: 100%;
      }
      &-content {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        flex-wrap: wrap;
        &--mobile {
          display: none;
          @media screen and (max-width: $lg) {
            display: flex;
          }
        }
        @media screen and (max-width: $lg) {
          &--desktop {
            display: none;
          }
        }
      }
      @media screen and (max-width: $xs) {
        &-content {
          flex-wrap: wrap;
        }
      }
    }
    &_title {
      font-size: 5.2rem;
      line-height: 1.25;
      @media screen and (max-width: $md) {
        font-size: 35px;
      }
    }
  }
  &__empty {
    @include emptyMessage();
  }

  &__button-box {
    @include centeredButtonBox();
  }
}
</style>

